import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { signIn, loading } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const { error } = await signIn(email, password);
    if (error) {
      setSubmitError(error.message);
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4 md:px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <span className="text-2xl font-bold font-heading text-primary-600">CotIdel.</span>
          </Link>
          <h1 className="text-xl font-bold font-heading text-foreground-950 mt-4 mb-2">Connexion</h1>
          <p className="text-sm text-foreground-500">Accédez à votre espace IDEL sécurisé.</p>
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {submitError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700 flex items-start gap-2">
                <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
                <span>{submitError}</span>
              </div>
            )}

            <div>
              <label htmlFor="email" className="text-xs font-semibold text-foreground-700 block mb-1.5">
                Adresse email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 bg-background-100 border border-background-300/60 rounded-md text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                placeholder="votre@email.fr"
              />
            </div>

            <div>
              <label htmlFor="password" className="text-xs font-semibold text-foreground-700 block mb-1.5">
                Mot de passe
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 pr-10 bg-background-100 border border-background-300/60 rounded-md text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-500 hover:bg-primary-600 text-white font-semibold px-4 py-3 rounded-md text-sm transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Connexion...
                </span>
              ) : (
                'Se connecter'
              )}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-background-200 text-center">
            <p className="text-xs text-foreground-500">
              Pas encore de compte ?{' '}
              <Link to="/auth/register" className="text-primary-600 font-semibold hover:text-primary-700">
                Créer un compte gratuit
              </Link>
            </p>
          </div>
        </div>

        <p className="text-center mt-6 text-xs text-foreground-400">
          <Link to="/" className="hover:text-foreground-600">
            <i className="ri-arrow-left-line mr-1"></i>
            Retour à l&apos;accueil
          </Link>
        </p>
      </div>
    </div>
  );
}